import { Box, Button, SxProps, Theme, useTheme } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchToggleType } from "@outschool/business-rules/dist-cjs/Search";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { timeOfDayToStartEndTimes } from "@outschool/time";
import {
  DayOfWeekSelector,
  Filter,
  FilterDropdown,
  displayTimeFromCardinalHour,
  useActivityString,
  useSearchFiltersString
} from "@outschool/ui-components-website";
import {
  Container,
  OverlayBackdrop
} from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import { isEqual, isNull, omitBy, pick, startCase, uniq } from "lodash";
import React from "react";
import ReactDOM from "react-dom";
import { useDebouncedCallback } from "use-debounce";

import * as Activity from "../../../shared/Activity";
import { ExtendedSearchFiltersType } from "../../lib/useSearchFilters";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import AgeFilters from "../search/AgeFilters";
import CreditFilter from "../search/CreditFilter";
import DateFilters from "../search/DateFilters";
import { GradeLevelFilter } from "../search/desktop/LevelFilters";
import { ThemeFilter } from "../search/desktop/ListingFilters";
import PriceFilter from "../search/PriceFilter";
import SessionDurationFilter from "../search/SessionDurationFilter";
import {
  TIMES_OF_DAY,
  TimeOfDaySelector,
  WeeklyMeetingsFilter,
  timeOfDayString
} from "../search/TimeFilters";
import { useShowCredits } from "../subscriptions/hooks/useShowCredits";

const WEEKLY_MEETING_FILTERS = ["weeklyMeetingsMin", "weeklyMeetingsMax"];

export default function TutorMatchFilters({
  filters,
  setFilters,
  categorySetFilters,
  showTheme = true
}: {
  filters: $TSFixMe;
  setFilters?: (
    filterCategory: string,
    partialFilters: ExtendedSearchFiltersType
  ) => void;
  categorySetFilters?: (payload: SearchFiltersType) => void;
  showTheme?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\onboardingTutorMatchFilters"
  );
  const isMobile = Screen.useIsMobile();
  const { timeZone } = useTimeZone();
  const theme = useTheme();
  const buttonSx: SxProps = {
    borderWidth: 0,
    boxShadow: "none",
    backgroundColor: theme.palette.primary[50],
    color: theme.palette.primary["main"],
    "&:active:not(:disabled), &:hover:not(:disabled), &:focus:not(:disabled)": {
      backgroundColor: theme.palette.primary["main"],
      color: "white"
    }
  };
  const { dateFiltersToString } = useSearchFiltersString();
  const [openFilterDropdown, setOpenFilterDropdown] = React.useState<
    string | null
  >(null);
  const [currentFilters, setCurrentFilters] = React.useState({
    ...filters
  });

  if (filters?.endBy && !filters.startBefore) {
    setFilters?.("Date", {
      endBy: undefined,
      startBefore: filters?.endBy
    });
  }

  const updateSearchFilters = (
    filterCategory: string,
    partialFilters: ExtendedSearchFiltersType
  ) => {
    setFilterChanges({
      filterCategory: filterChanges?.filterCategory
        ? "MultiFilter"
        : filterCategory,
      partialFilters: !filterChanges
        ? partialFilters
        : { ...filterChanges.partialFilters, ...partialFilters }
    });
  };

  const [filterChanges, setFilterChanges] = React.useState<{
    filterCategory: string;
    partialFilters: object;
  } | null>(null);

  const closeFilterDropdown = () => {
    if (filterChanges) {
      setFilters?.(filterChanges.filterCategory, filterChanges.partialFilters);
      categorySetFilters?.({ ...filterChanges.partialFilters });
      setFilterChanges(null);
    }

    setOpenFilterDropdown(null);
  };

  function ApplyButton() {
    return isMobile ? (
      <Button
        sx={{ marginTop: "0.5em" }}
        variant="contained"
        disabled={!!!filterChanges}
        onClick={closeFilterDropdown}
      >
        {t("Apply")}
      </Button>
    ) : (
      <></>
    );
  }

  const handleFilterChangeAndCloseFilterDropdown = (
    category: string,
    value: object
  ) => {
    setCurrentFilters({ ...currentFilters, ...value });
    setFilters?.(category, value);
    setOpenFilterDropdown(null);
  };

  React.useEffect(() => {
    const escapeFilter = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (filterChanges) {
          setFilterChanges(null);
        }
        if (openFilterDropdown) {
          setOpenFilterDropdown(null);
        }
      }
    };

    window.addEventListener("keydown", escapeFilter);

    return () => {
      window.removeEventListener("keydown", escapeFilter);
    };
  }, [filterChanges, openFilterDropdown]);

  const handleImplicitFilterChange = (category: string, value: object) => {
    updateSearchFilters(category, value);
    setCurrentFilters({ ...currentFilters, ...value });
  };

  const [isScrollBeingListened, setIsScrollBeingListened] =
    React.useState(false);
  const [debouncedCloseFilterDropdown] = useDebouncedCallback(() => {
    closeFilterDropdown();
  }, 100);
  if (openFilterDropdown && !isScrollBeingListened) {
    window.addEventListener("scroll", debouncedCloseFilterDropdown);
    setIsScrollBeingListened(true);
  }
  if (!openFilterDropdown && isScrollBeingListened) {
    window.removeEventListener("scroll", debouncedCloseFilterDropdown);
    setIsScrollBeingListened(false);
  }

  if (!filterChanges && !isEqual(currentFilters, filters)) {
    setCurrentFilters(filters);
  }
  const { translateThemeName } = useActivityString();

  const containerRef = React.useRef();
  const themes = currentFilters.theme ? currentFilters.theme.split(",") : [];
  const themesSummary = themes.map(translateThemeName).join(", ");

  const showCredits = useShowCredits();

  const ages: number[] = currentFilters.age
    ? currentFilters.age
        .split(",")
        .reduce((acc: Number[], current: $TSFixMe) => {
          const age = Number(current);
          if (age < Activity.ageMax) {
            acc.push(age);
          }
          return acc;
        }, [])
    : [];
  const gradeLevels: string[] = React.useMemo(() => {
    const defaultGradeLevels: string[] = currentFilters.gradeLevel
      ? currentFilters.gradeLevel.split(",")
      : [];
    return uniq(defaultGradeLevels).sort();
  }, [currentFilters.gradeLevel]);

  if (filters?.timeOfDay) {
    const { startAfterTime, endByTime } = timeOfDayToStartEndTimes(
      filters.timeOfDay
    );
    filters.startAfterTime = startAfterTime;
    filters.endByTime = endByTime;
    delete filters.timeOfDay;
  }

  const dateFilters = pick(currentFilters, [...SearchFilters.DATE_FILTERS]);
  const { startAfter, startBefore } = dateFilters;
  const defaultDateRange = {
    startAfter: Time.dayjs().format("YYYY-MM-DD"),
    startBefore: Time.dayjs().add(14, "days").format("YYYY-MM-DD")
  };

  const { weeklyMeetingsMin } = pick(currentFilters, [
    ...WEEKLY_MEETING_FILTERS
  ]);
  const scheduleFilters = pick(currentFilters, SearchFilters.TIME_FILTERS);
  const timeRangeValues =
    scheduleFilters.startAfterTime && scheduleFilters.endByTime
      ? [scheduleFilters.startAfterTime, scheduleFilters.endByTime].sort(
          (a, b) => a - b
        )
      : [Time.DAY_START_HOUR, Time.DAY_END_HOUR];
  const selectedTimeOfDayString = React.useMemo(() => {
    if (scheduleFilters.startAfterTime && scheduleFilters.endByTime) {
      const selected = TIMES_OF_DAY.find(({ range: [start, end] }) => {
        return (
          start === scheduleFilters.startAfterTime &&
          end === scheduleFilters.endByTime
        );
      });
      return selected
        ? timeOfDayString(selected.label, t)
        : `${displayTimeFromCardinalHour(
            scheduleFilters.startAfterTime
          )} - ${displayTimeFromCardinalHour(scheduleFilters.endByTime)}`;
    }
    return t("Time of Day");
  }, [scheduleFilters.startAfterTime, scheduleFilters.endByTime, t]);

  const weeklyMeetingString = React.useMemo(() => {
    if (weeklyMeetingsMin) {
      return t("{{weeklyMeetingsMin}}x per week", { weeklyMeetingsMin });
    }
    return t("Frequency");
  }, [weeklyMeetingsMin, t]);
  const priceFilters = omitBy(
    pick(
      currentFilters,
      SearchFilters.PRICE_PER_COURSE_FILTERS.concat(
        SearchFilters.PRICE_PER_MEETING_FILTERS
      )
    ),
    isNull
  );
  const priceCreditsFilters = omitBy(
    pick(currentFilters, SearchFilters.PRICE_CREDITS_PER_MEETING_FILTERS),
    isNull
  );
  return (
    <>
      {openFilterDropdown &&
        ReactDOM.createPortal(
          <OverlayBackdrop onClick={closeFilterDropdown} />,
          document.body
        )}
      <Box
        onClick={(e: $TSFixMe) => {
          if (e.target === e.currentTarget) {
            closeFilterDropdown();
          }
        }}
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.primary[50],
          display: "flex",
          overflowX: "auto",
          flexDirection: "column",
          gap: "1em",
          paddingY: "0.5em",
          position: "relative",
          zIndex: 200
        })}
        ref={containerRef}
      >
        <Container
          fullBleed={isMobile}
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <Box
              flex
              onClick={(e: $TSFixMe) => {
                if (e.target === e.currentTarget) {
                  closeFilterDropdown();
                }
              }}
              sx={{
                padding: isMobile || !showTheme ? "0px" : "0px 144px",
                gap: "1em"
              }}
              ref={containerRef}
            >
              {showTheme && (
                <FilterDropdown
                  filterName={t("Subject")}
                  filterValue={
                    themesSummary.length < 20
                      ? themesSummary
                      : t("{{numSubjects}} subject", {
                          count: themes.length,
                          numSubjects: themes.length,
                          defaultValue_plural: "{{numSubjects}} subjects"
                        })
                  }
                  open={openFilterDropdown === "theme"}
                  onClose={closeFilterDropdown}
                  onOpen={() => setOpenFilterDropdown("theme")}
                  buttonSx={buttonSx}
                >
                  <ThemeFilter
                    value={themes}
                    onChange={value => {
                      const newFilterValues = {
                        theme: value.length ? value.join(",") : null
                      };

                      handleImplicitFilterChange("Theme", newFilterValues);
                    }}
                    //1:1 subjects to show
                    selectedSearchToggle={SearchToggleType.TUTORING}
                  />
                  <ApplyButton />
                </FilterDropdown>
              )}
              <FilterDropdown
                filterName={t("Age")}
                filterValue={
                  ages.length
                    ? t("Age {{ages}}", {
                        count: ages.length,
                        ages: SearchFilters.agesArrayToString(
                          ages.map(a => a.toString())
                        ),
                        defaultValue_plural: "Ages {{ages}}"
                      })
                    : undefined
                }
                open={openFilterDropdown === "age"}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("age")}
                buttonSx={buttonSx}
              >
                <AgeFilters
                  value={ages}
                  handleChangesExternally
                  onChange={value => {
                    const newFilterValues = {
                      age: value.length ? value.join(",") : null
                    };
                    handleImplicitFilterChange("Age", newFilterValues);
                  }}
                  onCancel={() => {}}
                />
                <ApplyButton />
              </FilterDropdown>
              <GradeLevelFilter
                value={gradeLevels}
                onChange={value => {
                  const newFilterValues = {
                    gradeLevel: value.length ? value.join(",") : null
                  };
                  handleImplicitFilterChange("GradeLevel", newFilterValues);
                }}
                openFilterDropdown={openFilterDropdown}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("gradeLevel")}
                referenceElement={undefined}
                filterDropdownSx={buttonSx}
                applyButton={<ApplyButton />}
              />
              <FilterDropdown
                filterName={t("Time of Day")}
                //todo filter value
                filterValue={selectedTimeOfDayString}
                open={openFilterDropdown === "tod"}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("tod")}
                buttonSx={buttonSx}
              >
                <Filter title={t("Day of the week")}>
                  <DayOfWeekSelector
                    value={currentFilters.dow}
                    onChange={dow => {
                      handleImplicitFilterChange("schedule", { dow });
                    }}
                    showGroupSelection={true}
                    isDisabled={false}
                  />
                </Filter>
                <Filter
                  title={t("Time of day ({{timeZone}})", {
                    timeZone: startCase(Time.timeZoneName(timeZone))
                  })}
                  sx={{ marginTop: "8px", marginBottom: "4px", marginX: "4px" }}
                >
                  <TimeOfDaySelector
                    value={timeRangeValues}
                    onChange={([startAfterTime, endByTime]: [
                      number,
                      number
                    ]) => {
                      handleImplicitFilterChange("schedule", {
                        startAfterTime,
                        endByTime
                      });
                    }}
                    timesOfDay={TIMES_OF_DAY}
                    minTime={Time.DAY_START_HOUR}
                    maxTime={Time.DAY_END_HOUR}
                  />
                </Filter>
                <ApplyButton />
              </FilterDropdown>
              <SessionDurationFilter
                open={openFilterDropdown === "sessionDuration"}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("sessionDuration")}
                buttonSx={buttonSx}
                handleImplicitFilterChange={handleImplicitFilterChange}
                sessionDurationFilterValue={currentFilters}
                applyButton={<ApplyButton />}
              />
              <FilterDropdown
                filterName={t("Frequency")}
                filterValue={weeklyMeetingString}
                open={openFilterDropdown === "frequency"}
                onClose={() => setOpenFilterDropdown(null)}
                onOpen={() => setOpenFilterDropdown("frequency")}
                buttonSx={buttonSx}
              >
                <WeeklyMeetingsFilter
                  filters={scheduleFilters}
                  onChange={result => {
                    handleImplicitFilterChange("Schedule", result);
                  }}
                />
                <ApplyButton />
              </FilterDropdown>
              {/* no currently existing class duration filter? TODO add  */}
              <FilterDropdown
                filterName={t("Date")}
                filterValue={dateFiltersToString(
                  startAfter || startBefore ? dateFilters : defaultDateRange
                )}
                open={openFilterDropdown === "date"}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("date")}
                buttonSx={buttonSx}
              >
                <DateFilters
                  value={
                    startAfter || startBefore ? dateFilters : defaultDateRange
                  }
                  handleChangesExternally
                  onShortcutSelected={result => {
                    const { startAfter, startBefore } = result;
                    const newFilterValues = !(startAfter || startBefore)
                      ? {
                          startAfter: Time.dayjs().format("YYYY-MM-DD"),
                          startBefore: undefined
                        }
                      : result;
                    handleFilterChangeAndCloseFilterDropdown(
                      // eslint-disable-next-line i18next/no-literal-string
                      "Date",
                      newFilterValues
                    );
                  }}
                  onChange={result => {
                    handleImplicitFilterChange("Date", result);
                  }}
                  onCancel={closeFilterDropdown}
                />
                <ApplyButton />
              </FilterDropdown>
              {showCredits ? (
                <CreditFilter
                  creditFilterValue={priceCreditsFilters}
                  handleChangesExternally
                  open={openFilterDropdown === "priceCreditsPerMeeting"}
                  onOpen={() => setOpenFilterDropdown("priceCreditsPerMeeting")}
                  onClose={closeFilterDropdown}
                  buttonSx={buttonSx}
                  handleImplicitFilterChange={handleImplicitFilterChange}
                  isCourse={false}
                />
              ) : (
                <PriceFilter
                  priceFilterValue={priceFilters}
                  open={openFilterDropdown === "price"}
                  onClose={closeFilterDropdown}
                  onOpen={() => setOpenFilterDropdown("price")}
                  selectedSearchToggle={SearchToggleType.TUTORING}
                  buttonSx={buttonSx}
                  handleImplicitFilterChange={handleImplicitFilterChange}
                  handleChangesExternally={true}
                />
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}
